import React, { ReactNode } from 'react';
import { Button, ButtonProps } from '@mui/material';

export type FileViewerBoxButtonTone = 'default' | 'ai' | 'placeholder';

export interface FileViewerBoxButtonProps
  extends Omit<ButtonProps, 'variant' | 'startIcon' | 'endIcon'> {
  icon?: ReactNode;
  selected?: boolean;
  tone?: FileViewerBoxButtonTone;
  iconSize?: number;
  boxSize?: number;
}

const getStateColors = (tone: FileViewerBoxButtonTone, selected: boolean) => {
  if (tone === 'ai') {
    return {
      backgroundColor: selected ? 'rgba(199, 122, 196, 0.2)' : 'transparent',
      hoverBackgroundColor: selected
        ? 'rgba(199, 122, 196, 0.3)'
        : 'rgba(199, 122, 196, 0.1)',
      activeBackgroundColor: 'rgba(199, 122, 196, 0.35)',
      borderColor: 'transparent',
      color: '#FFFFFF',
    };
  }

  if (tone === 'placeholder') {
    return {
      backgroundColor: '#2E3338',
      hoverBackgroundColor: '#2E3338',
      activeBackgroundColor: '#464C53',
      borderColor: selected ? '#FFFFFF' : '#464C53',
      color: selected ? '#FFFFFF' : '#8A949E',
    };
  }

  return {
    backgroundColor: selected ? '#2E3338' : 'transparent',
    hoverBackgroundColor: selected ? '#3D4349' : '#2E3338',
    activeBackgroundColor: '#464C53',
    borderColor: 'transparent',
    color: '#FFFFFF',
  };
};

const BoxButton: React.FC<FileViewerBoxButtonProps> = ({
  icon,
  children,
  selected = false,
  tone = 'default',
  iconSize = 20,
  boxSize = 28,
  sx,
  ...props
}) => {
  const width = `${boxSize}px`;
  const height = `${boxSize}px`;
  const hasCustomContent = Boolean(children);
  const colors = getStateColors(tone, selected);

  return (
    <Button
      type="button"
      {...props}
      sx={[
        {
          minWidth: hasCustomContent ? 'auto' : width,
          width: hasCustomContent ? 'auto' : width,
          height,
          borderRadius: '8px',
          padding: hasCustomContent ? '0 12px' : 0,
          gap: '4px',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: colors.borderColor,
          color: colors.color,
          backgroundColor: colors.backgroundColor,
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '20px',
          whiteSpace: 'nowrap',
          '& .FileViewerBoxButton-icon svg': {
            display: 'block',
            width: iconSize,
            height: iconSize,
          },
          '&:hover': {
            borderColor:
              tone === 'placeholder' ? '#FFFFFF' : colors.borderColor,
            backgroundColor: colors.hoverBackgroundColor,
          },
          '&:active': {
            backgroundColor: colors.activeBackgroundColor,
          },
          '&.Mui-disabled': {
            color: '#464C53',
            backgroundColor: tone === 'placeholder' ? '#2E3338' : 'transparent',
            borderColor: tone === 'placeholder' ? '#464C53' : 'transparent',
            // stroke로 그린 아이콘과 fill로 그린 아이콘(rotate의 "90", expand)이
            // 섞여 있어 각각 실제로 쓰인 속성만 덮어쓴다.
            '& path[stroke]': {
              stroke: '#464C53',
            },
            '& path[fill]:not([fill="none"])': {
              fill: '#464C53',
            },
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {icon ? (
        <span className="FileViewerBoxButton-icon">{icon}</span>
      ) : (
        children
      )}
    </Button>
  );
};

export default BoxButton;
